import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import React, { useEffect, useRef } from 'react'

import { LEARNING_ACCOUNT_TYPE } from 'constants/accountTypes'
import { LOGIN, MY_5_DYNAMICS, formatRoute } from 'constants/urls'
import { PERSON } from 'props'
import { setCurrentProfileByAccountId } from 'app_modules/session/actions'
import * as accountSelectors from 'app_modules/accounts/selectors'
import * as sessionSelectors from 'app_modules/session/selectors'
import { DefaultLayout } from 'constants/routeLayouts'
import SuspenseWithErrorBoundary from 'generics/SuspenseWithErrorBoundary'
import RouteFallback from './RouteFallback'

const isLazyComponent = component =>
  component && typeof component === 'object' && component.$$typeof === Symbol.for('react.lazy')

const Route = ({
  component: Comp,
  dispatch,
  isAuthenticated,
  isStudentWaitingResults,
  layout,
  permissions,
  private: isPrivate,
  profile,
  validatePersonalAccount,
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams()

  // Keep stable refs so effects only re-run when accountId param changes,
  // not on every store update.
  const dispatchRef = useRef(dispatch)
  const isAuthenticatedRef = useRef(isAuthenticated)
  const isPrivateRef = useRef(isPrivate)
  const prevAccountIdRef = useRef(null)
  dispatchRef.current = dispatch
  isAuthenticatedRef.current = isAuthenticated
  isPrivateRef.current = isPrivate

  // Dispatch setCurrentProfileByAccountId only when the URL accountId changes.
  const { accountId: accountIdParam } = params
  useEffect(() => {
    if (!isPrivateRef.current || !isAuthenticatedRef.current) return
    if (!accountIdParam || accountIdParam === prevAccountIdRef.current) return
    prevAccountIdRef.current = accountIdParam
    dispatchRef.current(setCurrentProfileByAccountId(parseInt(accountIdParam, 10)))
  }, [accountIdParam])

  // Scroll to top on every route change.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // Permissions guard: navigate(-1) must be in useEffect — calling navigate()
  // during render causes infinite loops in React 18.
  const lacksPermissions = Boolean(permissions && profile && !profile.can(permissions))
  const navigateRef = useRef(navigate)
  navigateRef.current = navigate
  useEffect(() => {
    if (lacksPermissions) navigateRef.current(-1)
  }, [lacksPermissions])

  // Auth guard: use useEffect + navigate instead of <Navigate> JSX.
  // <Navigate> calls history.replace() on every render it is returned from,
  // which dispatches LOCATION_CHANGE to Redux on every render, which
  // re-renders this component again — infinite loop on logout.
  const shouldRedirectToLogin = isPrivate && !isAuthenticated
  const redirectedRef = useRef(false)
  useEffect(() => {
    if (shouldRedirectToLogin && !redirectedRef.current) {
      redirectedRef.current = true
      navigate(LOGIN, { replace: true, state: { from: location } })
    }
    if (!shouldRedirectToLogin) redirectedRef.current = false
  }, [shouldRedirectToLogin]) // eslint-disable-line react-hooks/exhaustive-deps

  if (shouldRedirectToLogin) return null

  // Render nothing while profile loads.
  if (!profile || !profile.account) return null

  if (lacksPermissions) return null

  const { accountType, id: accountId, flags } = profile.account

  const {
    administrationTab: isAdministrationTabVisible,
    networkTab: isNetworkTabVisible,
    newsTab: isNewsTabVisible,
    teamsTab: isTeamsTabVisible,
    tokenAdministrationTab: isTokenAdministrationTabVisible,
    trainingCenterTab: isTrainingCenterTabVisible,
  } = flags || {}

  const isLearningAccount = accountType === LEARNING_ACCOUNT_TYPE

  const learningMenuTabsVisibility = {
    administration: isAdministrationTabVisible,
    'administration/people': isAdministrationTabVisible,
    'administration/content-modules': isAdministrationTabVisible,
    'administration/customization-modules': isAdministrationTabVisible,
    'my-5-dynamics': true,
    network: isNetworkTabVisible,
    news: isNewsTabVisible,
    relationships: !isStudentWaitingResults,
    teams: isTeamsTabVisible && !isStudentWaitingResults,
    'token-administration': isTokenAdministrationTabVisible,
    'token-administration/people': isTokenAdministrationTabVisible,
    'training-center': isTrainingCenterTabVisible,
  }

  const canNavigate = learningMenuTabsVisibility[`${location.pathname.replace(`/account/${accountId}/`, '')}`]

  if (isLearningAccount && !canNavigate && canNavigate !== undefined) {
    return <Navigate to={formatRoute(MY_5_DYNAMICS, { accountId })} state={{ from: location }} replace />
  }

  if (validatePersonalAccount) {
    const { isPersonal: isPersonalAccount } = profile.account
    if (isPersonalAccount) {
      return <Navigate to={formatRoute(MY_5_DYNAMICS, { accountId })} state={{ from: location }} replace />
    }
  }

  const Layout = layout || DefaultLayout

  const history = {
    push: path => navigate(path),
    replace: path => navigate(path, { replace: true }),
    go: delta => navigate(delta),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
  }

  const page = (
    <SuspenseWithErrorBoundary fallback={<RouteFallback />}>
      <Comp match={{ params }} location={location} history={history} />
    </SuspenseWithErrorBoundary>
  )

  // DefaultLayout wraps the page outside Suspense so header/footer stay visible
  // while the lazy route chunk loads. Lazy alternate layouts get an outer Suspense.
  if (isLazyComponent(Layout)) {
    return (
      <SuspenseWithErrorBoundary fallback={<RouteFallback />}>
        <Layout>{page}</Layout>
      </SuspenseWithErrorBoundary>
    )
  }

  return <Layout>{page}</Layout>
}

Route.propTypes = {
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.elementType]).isRequired,
  dispatch: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  isStudentWaitingResults: PropTypes.bool,
  layout: PropTypes.oneOfType([PropTypes.func, PropTypes.elementType]),
  permissions: PropTypes.string,
  private: PropTypes.bool,
  profile: PERSON,
  validatePersonalAccount: PropTypes.bool,
}

Route.defaultProps = {
  isAuthenticated: false,
  isStudentWaitingResults: false,
  layout: null,
  permissions: null,
  private: false,
  profile: null,
  validatePersonalAccount: false,
}

const mapStateToProps = state => ({
  isAuthenticated: sessionSelectors.isAuthenticated(state),
  isStudentWaitingResults: accountSelectors.isStudentWaitingResults(state),
  profile: sessionSelectors.currentProfile(state),
})

export default connect(mapStateToProps)(Route)
